/*
 * TeamMembersSection — sales representatives management for the workspace.
 *
 * Workspace admin / admin_enterprise can:
 *   - Create a sales representative user (gated by `multi_user` feature)
 *   - List / activate-deactivate / remove existing reps
 * Created reps automatically appear in LeadDetailPage → "Représentant assigné".
 *
 * NOTE: This component intentionally does NOT touch the global Admin panel,
 * the roles config, or the permissions system. It only wraps the dedicated
 * workspace member endpoints.
 */

import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Users, Plus, Copy, Trash2, Mail, Phone, Loader2, ShieldCheck } from 'lucide-react';

export function TeamMembersSection({ user, tf }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [createdCreds, setCreatedCreds] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/workspaces/members');
      setMembers(Array.isArray(data) ? data : []);
    } catch {
      // empty list is the safe fallback
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(tf('Nom et email requis', 'Name and email required'));
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/workspaces/members', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
      });
      toast.success(tf('Représentant créé', 'Representative created'));
      // Show the generated password ONCE so the owner can hand it over.
      if (data.initial_password) {
        setCreatedCreds({ email: data.email, password: data.initial_password });
      }
      setShowCreate(false);
      setForm({ name: '', email: '', phone: '' });
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'feature_locked') {
        toast.error(tf(
          'Disponible avec les forfaits Pro et Enterprise.',
          'Available on Pro and Enterprise plans.',
        ));
      } else {
        toast.error((typeof d === 'string' ? d : null) || tf('Erreur', 'Error'));
      }
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/workspaces/members/${m.user_id}`, { is_active: !m.is_active });
      toast.success(tf('Mis à jour', 'Updated'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || tf('Erreur', 'Error'));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(tf(
      `Retirer ${m.name || m.email} du workspace ?`,
      `Remove ${m.name || m.email} from this workspace?`,
    ))) return;
    try {
      await api.delete(`/workspaces/members/${m.user_id}`);
      toast.success(tf('Représentant retiré', 'Representative removed'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || tf('Erreur', 'Error'));
    }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success(tf('Copié', 'Copied')); } catch { /* noop */ }
  };

  return (
    <>
      <Card data-testid="team-members-section">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {tf('Représentants', 'Representatives')}
              </CardTitle>
              <CardDescription>
                {tf(
                  'Créez les représentants commerciaux de votre workspace. Ils apparaîtront dans le sélecteur "Représentant assigné" de chaque lead.',
                  'Create the sales representatives for your workspace. They will appear in the "Assigned representative" selector on each lead.',
                )}
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreate(true)} data-testid="add-member-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              {tf('Ajouter un représentant', 'Add representative')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Owner */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-bold text-primary">{user?.name?.charAt(0).toUpperCase() || '?'}</span>
                </div>
                <div>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <Badge variant="default"><ShieldCheck className="h-3 w-3 mr-1" />{tf('Propriétaire', 'Owner')}</Badge>
            </div>
            <Separator />

            {loading ? (
              <div className="py-6 text-center text-muted-foreground text-sm">
                <Loader2 className="h-5 w-5 inline animate-spin mr-2" />{tf('Chargement...', 'Loading...')}
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm" data-testid="no-members-empty">
                {tf('Aucun représentant pour l\'instant.', 'No representatives yet.')}
              </div>
            ) : (
              <ul className="space-y-2" data-testid="members-list">
                {members.map((m) => (
                  <li
                    key={m.user_id}
                    className="flex items-center justify-between p-3 border rounded-lg gap-3"
                    data-testid={`member-${m.user_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{m.name || m.email}</p>
                        {!m.is_active && (
                          <Badge variant="secondary" className="text-xs">{tf('Inactif', 'Inactive')}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</span>
                        {m.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{m.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(m)}
                        data-testid={`toggle-active-${m.user_id}`}
                      >
                        {m.is_active ? tf('Désactiver', 'Deactivate') : tf('Activer', 'Activate')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(m)}
                        data-testid={`remove-${m.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!creating) setShowCreate(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="create-member-dialog">
          <DialogHeader>
            <DialogTitle>{tf('Nouveau représentant', 'New representative')}</DialogTitle>
            <DialogDescription>
              {tf(
                'Un mot de passe temporaire sera généré. Vous pourrez le copier après création.',
                'A temporary password will be generated. You can copy it after creation.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="member-name">{tf('Nom complet', 'Full name')}</Label>
              <Input
                id="member-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={tf('ex: Sara Vendor', 'e.g. Sara Vendor')}
                data-testid="member-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="member-email">{tf('Email', 'Email')}</Label>
              <Input
                id="member-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="sara@duramax.ca"
                data-testid="member-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="member-phone">{tf('Téléphone (optionnel)', 'Phone (optional)')}</Label>
              <Input
                id="member-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="514-555-1234"
                data-testid="member-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>
              {tf('Annuler', 'Cancel')}
            </Button>
            <Button onClick={create} disabled={creating} data-testid="save-member-btn">
              {creating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{tf('Création...', 'Creating...')}</> : tf('Créer', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog — shown once after creation */}
      <Dialog open={!!createdCreds} onOpenChange={(o) => { if (!o) setCreatedCreds(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="member-credentials-dialog">
          <DialogHeader>
            <DialogTitle>{tf('Identifiants temporaires', 'Temporary credentials')}</DialogTitle>
            <DialogDescription>
              {tf(
                'Copiez ces informations et transmettez-les au représentant. Le mot de passe ne sera plus affiché ensuite.',
                'Copy these and send them to the rep. The password will not be shown again.',
              )}
            </DialogDescription>
          </DialogHeader>
          {createdCreds && (
            <div className="space-y-2 py-1">
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded-md">
                <span className="text-xs"><strong>Email:</strong> {createdCreds.email}</span>
                <Button size="sm" variant="ghost" onClick={() => copy(createdCreds.email)}><Copy className="h-3 w-3" /></Button>
              </div>
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded-md">
                <span className="text-xs">
                  <strong>{tf('Mot de passe', 'Password')}:</strong>{' '}
                  <span
                    className="font-mono tracking-wider select-all px-1.5 py-0.5 bg-background rounded border"
                    data-testid="member-temp-password"
                  >
                    {createdCreds.password}
                  </span>
                </span>
                <Button size="sm" variant="ghost" onClick={() => copy(createdCreds.password)}><Copy className="h-3 w-3" /></Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {tf(
                  'Astuce : utilisez le bouton « Copier » pour éviter toute erreur de saisie.',
                  'Tip: use the "Copy" button to avoid typing errors.'
                )}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreatedCreds(null)}>{tf('Fermer', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
